import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IndianRupee, TrendingUp, ShieldAlert, UserCheck, Sparkles, ArrowRight, Activity, FlaskConical } from "lucide-react";
import api from "@/lib/api";

const PHASE_TONE = { "Phase I": "sky", "Phase II": "mint", "Phase III": "peach", "Phase IV": "lavender" };
const VERDICT_TONE = { green: "mint", amber: "peach", red: "rose" };
const TIER_TONE = { Platinum: "lavender", Gold: "peach", Silver: "sky" };

export default function CroInsightsInfographic() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [ent, port, pred, inv] = await Promise.all([
          api.get("/cro/enterprise-analytics"),
          api.get("/cro/portfolio"),
          api.get("/cro/predictions"),
          api.get("/cro/investigators"),
        ]);
        setData({
          ent: ent.data,
          portfolio: port.data,
          predictions: pred.data,
          investigators: inv.data,
        });
      } catch (e) {
        setData({ error: true });
      }
    })();
  }, []);

  if (!data) return (
    <div className="rounded-2xl border border-border bg-gradient-to-br from-pastel-sky-bg via-pastel-mint-bg to-pastel-lavender-bg p-8 mb-6" data-testid="cro-insights-loading">
      <div className="text-sm text-muted-foreground animate-pulse">Loading CRO Insights…</div>
    </div>
  );
  if (data.error) return null;

  const { ent, portfolio, predictions, investigators } = data;
  const totals = ent.totals;
  const phaseMix = countBy(portfolio.studies || [], "phase");
  const taChips = uniqueValues(portfolio.studies || [], "therapeutic_area");
  const topRisk = [...predictions.per_study]
    .sort((a, b) => a.success_probability_pct - b.success_probability_pct)
    .slice(0, 3);
  const tierCounts = {
    Platinum: investigators.totals.platinum,
    Gold:     investigators.totals.gold,
    Silver:   investigators.totals.silver,
  };
  const totalPI = investigators.totals.investigators;
  const onTime = totals.on_time_delivery_pct;

  return (
    <div className="rounded-2xl border border-border bg-gradient-to-br from-pastel-sky-bg via-white to-pastel-lavender-bg p-6 lg:p-8 mb-8" data-testid="cro-insights-infographic">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="overline text-primary/70 flex items-center gap-1.5">
            <FlaskConical size={12} /> Clinical Research Intelligence · Live snapshot
          </div>
          <h2 className="mt-1 font-serif text-2xl lg:text-3xl text-primary">CRO Insights</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Cross-portfolio KPIs powering the 20-layer CRO/CTO Stack. Explore the full dashboard for study-by-study detail.
          </p>
        </div>
        <Link
          to="/cro"
          data-testid="cro-insights-open"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border border-primary bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
        >
          Open CRO Stack <ArrowRight size={12} />
        </Link>
      </div>

      {/* Row 1 · headline KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <InfoTile icon={IndianRupee} tone="sky" label="Portfolio value" value={`₹${totals.budget_cr} Cr`} sub={`${totals.studies} studies · ${totals.sites} sites`} testid="cro-info-value" />
        <InfoTile icon={TrendingUp} tone="mint" label="Revenue · ROI" value={`₹${totals.revenue_cr} Cr`} sub={`${totals.roi_pct}% ROI`} testid="cro-info-revenue" />
        <Gauge label="On-time delivery" pct={onTime} testid="cro-info-ontime" />
        <InfoTile icon={UserCheck} tone="lavender" label="Investigator network" value={`${totalPI}`} sub={`${tierCounts.Platinum} Platinum`} testid="cro-info-pi" />
      </div>

      {/* Row 2 · visual splits */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-white p-4" data-testid="cro-info-phase-mix">
          <div className="overline mb-2">Studies by phase</div>
          <div className="space-y-2">
            {Object.entries(phaseMix).map(([phase, n]) => (
              <div key={phase} className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full bg-pastel-${PHASE_TONE[phase] || "sky"}-fg`} />
                <span className="text-xs text-primary flex-1">{phase}</span>
                <span className="text-xs font-mono text-muted-foreground">{n}</span>
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden max-w-[60%]">
                  <div className={`h-full bg-pastel-${PHASE_TONE[phase] || "sky"}-fg`} style={{ width: `${(n / (portfolio.studies?.length || 1)) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-border">
            <div className="overline mb-1.5">Therapeutic areas</div>
            <div className="flex flex-wrap gap-1">
              {taChips.map((ta) => (
                <span key={ta} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-pastel-lemon-bg text-pastel-lemon-fg border border-pastel-lemon-border">{ta}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-white p-4" data-testid="cro-info-top-risk">
          <div className="overline mb-2 flex items-center gap-1"><ShieldAlert size={11} /> Highest-risk studies</div>
          <ul className="space-y-2.5">
            {topRisk.map((s) => (
              <li key={s.id} className="text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-primary font-medium truncate">{s.name}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-pastel-${VERDICT_TONE[s.verdict]}-bg text-pastel-${VERDICT_TONE[s.verdict]}-fg`}>
                    {s.verdict}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
                  <Activity size={10} /> Success {s.success_probability_pct}%
                  <span>·</span>
                  <span>Dropout {s.dropout_risk_pct}%</span>
                </div>
                <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${s.verdict === "green" ? "bg-secondary" : s.verdict === "amber" ? "bg-accent" : "bg-destructive"}`} style={{ width: `${s.success_probability_pct}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-border bg-white p-4" data-testid="cro-info-pi-stack">
          <div className="overline mb-2 flex items-center gap-1"><UserCheck size={11} /> Investigator tiers</div>
          <div className="flex h-6 rounded-full overflow-hidden bg-muted mb-2">
            {["Platinum", "Gold", "Silver"].map((tier) =>
              tierCounts[tier] > 0 ? (
                <div key={tier} className={`h-full bg-pastel-${TIER_TONE[tier]}-fg`} style={{ width: `${(tierCounts[tier] / Math.max(totalPI, 1)) * 100}%` }} title={`${tier}: ${tierCounts[tier]}`} />
              ) : null
            )}
          </div>
          <div className="space-y-1 text-xs">
            {["Platinum", "Gold", "Silver"].map((tier) => (
              <div key={tier} className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-primary">
                  <span className={`w-2 h-2 rounded-full bg-pastel-${TIER_TONE[tier]}-fg`} />
                  {tier}
                </span>
                <span className="font-mono text-muted-foreground">{tierCounts[tier]}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-border">
            <div className="overline mb-1.5 flex items-center gap-1"><Sparkles size={10} /> Avg scorecard</div>
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-2xl text-primary">{investigators.totals.avg_score}</span>
              <span className="text-[10px] text-muted-foreground">/100 across {totalPI} PIs</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoTile({ icon: Icon, tone, label, value, sub, testid }) {
  return (
    <div className={`rounded-xl border border-pastel-${tone}-border bg-pastel-${tone}-bg p-4`} data-testid={testid}>
      <div className={`overline text-pastel-${tone}-fg/80 flex items-center gap-1.5`}>
        <Icon size={11} />
        {label}
      </div>
      <div className={`mt-1 font-serif text-2xl text-pastel-${tone}-fg`}>{value}</div>
      {sub && <div className={`text-[10px] text-pastel-${tone}-fg/70 mt-0.5`}>{sub}</div>}
    </div>
  );
}

function Gauge({ label, pct, testid }) {
  const tone = pct >= 85 ? "mint" : pct >= 70 ? "peach" : "rose";
  return (
    <div className={`rounded-xl border border-pastel-${tone}-border bg-pastel-${tone}-bg p-4 flex flex-col justify-between`} data-testid={testid}>
      <div className={`overline text-pastel-${tone}-fg/80`}>{label}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className={`font-serif text-2xl text-pastel-${tone}-fg`}>{pct}</span>
        <span className={`text-xs text-pastel-${tone}-fg/70`}>%</span>
      </div>
      <div className={`mt-2 h-1.5 rounded-full bg-white/40 overflow-hidden`}>
        <div className={`h-full bg-pastel-${tone}-fg`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

function countBy(arr, key) {
  return arr.reduce((acc, x) => {
    acc[x[key]] = (acc[x[key]] || 0) + 1;
    return acc;
  }, {});
}
function uniqueValues(arr, key) {
  return Array.from(new Set(arr.map((x) => x[key]).filter(Boolean)));
}
